import { execFileSync } from 'child_process'
import fs from 'fs'
import path from 'path'

import { packageRoot } from '../templates/package-root'
import { hasGlobalInstall, resetCliCommandCache } from '../utils/invocation'
import { say, sayWarning, section } from './output'
import { confirm } from './prompts/confirm'

// The one thing `init` cannot do by writing files: put `flowrunner` on PATH.
//
// A project-local install is never on an interactive shell's PATH — npm only puts `node_modules/.bin` there
// for its own lifecycle (`npm run`, `npx`). So without a global entry the name does not resolve, which is
// what made the old "flowrunner deploy -s <id>" hint un-runnable for anyone who had not installed one.
//
// The global copy is inert: `bin/flowrunner.js` hands every invocation to the project's own copy
// (see bin/resolve-local.js), so installing one cannot pin a project to the wrong version. That is what
// makes offering it safe.

/** The version of the CLI running right now — the same one `init` writes into `devDependencies`. */
function runningVersion(): string {
  return JSON.parse(fs.readFileSync(path.join(packageRoot(), 'package.json'), 'utf-8')).version
}

/**
 * Offer to install the launcher globally, and do it if the answer is yes. Returns whether it was installed.
 *
 * Skipped silently when one is already there — re-installing would downgrade a newer global to whatever
 * version happens to be scaffolding, for no gain, since the global's version does not affect any project.
 *
 * A failure here is a warning, never a failed `init`: the project on disk is already correct, and the `npx`
 * form works without any of this. The usual cause is a global prefix the user cannot write to (`EACCES` on
 * a system-installed Node), which is a machine-setup problem and not something to abort a scaffold over.
 */
export async function offerGlobalInstall(): Promise<boolean> {
  if (!process.stdin.isTTY || hasGlobalInstall()) {
    return false
  }

  section('The `flowrunner` command is not on your PATH yet.')
  say('  Installing it globally lets you run `flowrunner cs` instead of `npx flowrunner cs`.')
  say("  It only launches your project's own copy, so it never decides which version a project uses.")

  if (!await confirm('\nInstall it?', true)) {
    return false
  }

  const npm = process.platform === 'win32' ? 'npm.cmd' : 'npm'
  const version = runningVersion()

  say('')

  try {
    execFileSync(npm, ['install', '--global', `flowrunner-cli@${ version }`], { stdio: 'inherit' })

    // The bare command resolves from here on, so the next-steps hints should print it rather than `npx`.
    resetCliCommandCache()

    return true
  } catch {
    sayWarning(`Could not install globally — carry on with \`npx flowrunner\`, or run this yourself:
  npm install --global flowrunner-cli@${ version }`)

    return false
  }
}
